import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { PurgeSettings } from 'types';
import { useUpdatePurgeSettings } from './api/updatePurgeSettings';

interface Props {
  open: boolean;
  handleClose: () => void;
  settings: PurgeSettings | undefined;
}

const PurgeSettingsDialog = ({ open, handleClose, settings }: Props) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      purge_enabled: false,
      purge_interval_value: 1,
      purge_interval_unit: 'months',
    },
  });

  const updatePurgeSettings = useUpdatePurgeSettings({});

  useEffect(() => {
    if (settings) {
      const splitInterval = settings.purge_interval.split(' ');
      const intervalValue = splitInterval[0];
      const intervalUnit = splitInterval[1];
      setValue('purge_enabled', !!settings.purge_enabled);
      setValue('purge_interval_value', Number(intervalValue));
      setValue('purge_interval_unit', intervalUnit);
    } else reset();
  }, [settings, open, reset, setValue]);

  const onSubmit = async () => {
    const formState = getValues();
    await updatePurgeSettings.mutateAsync({
      data: {
        purge_enabled: formState.purge_enabled,
        purge_interval:
          formState.purge_interval_value + ' ' + formState.purge_interval_unit,
      },
    });
    reset();
    handleClose();
  };

  const handleCancel = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Edit Purge Settings</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction='column'
          style={{ paddingTop: '.4rem' }}
        >
          <Grid item container xs justifyContent='center'>
            <Controller
              name='purge_enabled'
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  label='Purge Enabled'
                  control={<Checkbox checked={!!value} onChange={onChange} />}
                />
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='purge_interval_value'
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='purge_interval_value'
                  label='Purge Interval'
                  name='purge_interval_value'
                  value={value}
                  onChange={onChange}
                  fullWidth
                  required
                  inputProps={{ type: 'number' }}
                  error={!!errors.purge_interval_value}
                  disabled={!watch('purge_enabled')}
                />
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='purge_interval_unit'
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel id='unit-select-label'>Unit</InputLabel>
                  <Select
                    labelId='unit-select-label'
                    label='Unit'
                    id='purge_interval_unit'
                    name='purge_interval_unit'
                    onChange={(e) => onChange(e.target.value)}
                    value={value}
                    disabled={!watch('purge_enabled')}
                  >
                    <MenuItem key='days' value={'days'}>
                      Days
                    </MenuItem>
                    <MenuItem key='months' value={'months'}>
                      Months
                    </MenuItem>
                    <MenuItem key='years' value={'years'}>
                      Years
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSubmit(onSubmit)}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurgeSettingsDialog;
